import { isEmpty } from "@/utils";
import {
  colorMD,
  colorTheme,
  variantNumber,
  variantType,
  getMDColorClass
} from "@/design/colors/mdColors";

/**
 * Variant Types
 * @type {(string)[]}
 */
const variantTypes = Object.freeze([variantType.lighten, variantType.darken]);

/**
 * Variant Numbers
 * @type {number[]}
 */
const variantNumbers = Object.freeze([
  variantNumber.n1,
  variantNumber.n2,
  variantNumber.n3,
  variantNumber.n4,
  variantNumber.n5
]);

/**
 * Get Success Color Class
 * @param {String|string} variantType
 * @param {String|string} variantNumber
 * @returns {string|*}
 */
const getSuccessColorClass = (
  variantType = variantType.lighten,
  variantNumber = variantNumber.n1
) => new Color(colorTheme.success, variantType, variantNumber).getClassColor();

/**
 * Get Error Color Class
 * @param {String|string} variantType
 * @param {String|string} variantNumber
 * @returns {string|*}
 */
const getErrorColorClass = (
  variantType = variantType.lighten,
  variantNumber = variantNumber.n1
) => new Color(colorTheme.error, variantType, variantNumber).getClassColor();

/**
 * Get Warning Color Class
 * @param {String|string} variantType
 * @param {String|string} variantNumber
 * @returns {string|*}
 */
const getWarningColorClass = (
  variantType = variantType.lighten,
  variantNumber = variantNumber.n1
) => new Color(colorTheme.warning, variantType, variantNumber).getClassColor();

/**
 * Get Primary Color Class
 * @param {String|string} variantType
 * @param {String|string} variantNumber
 * @returns {string|*}
 */
const getPrimaryColorClass = (
  variantType = variantType.lighten,
  variantNumber = variantNumber.n1
) => new Color(colorTheme.primary, variantType, variantNumber).getClassColor();

/**
 * get Secondary Color Class
 * @param {String|string} variantType
 * @param {String|string} variantNumber
 * @returns {string|*}
 */
const getSecondaryColorClass = (
  variantType = variantType.lighten,
  variantNumber = variantNumber.n1
) =>
  new Color(colorTheme.secondary, variantType, variantNumber).getClassColor();

/**
 * Abstract Material Design Colors specification
 * https://vuetifyjs.com/en/styles/colors/
 */
class Color {
  name = undefined;
  variant = "";
  variantNumber = 0;

  constructor(name = undefined, variant = "", variantNumber = 0) {
    this.name = name;
    this.variant = variant;
    this.variantNumber = variantNumber;
  }

  /**
   * Get Class Color
   * @returns {string|*}
   */
  getClassColor() {
    if (
      isEmpty(this.name) ||
      isEmpty(this.variant) ||
      !this.isValidVariantNumber(this.variantNumber) ||
      !this.isValidVariantType(this.variant)
    ) {
      return this.name;
    }

    return getMDColorClass(this.name, this.variant, this.variantNumber);
  }

  /**
   * Determines is Valid Variant Number
   * @param number
   * @returns {boolean}
   */
  isValidVariantNumber(number) {
    return !isEmpty(variantNumbers.find(el => el === number));
  }

  /**
   * Determines is Valid Variant Type
   * @param variant
   * @returns {boolean}
   */
  isValidVariantType(variant) {
    return !isEmpty(variantTypes.find(el => el === variant));
  }
}

export {
  variantType,
  variantNumber,
  colorTheme,
  colorMD,
  Color,
  getSuccessColorClass,
  getErrorColorClass,
  getWarningColorClass,
  getPrimaryColorClass,
  getSecondaryColorClass,
  getMDColorClass
};
